import React, { useEffect, useState } from 'react';
import Skeleton from '../components/Skeleton.js';
import { api } from '../lib/api.js';
import { useAuthStore } from '../stores/useAuthStore.js';
import { connectSocket, getSocket } from '../lib/socket.js';
import Badge from '../components/Badge.js';
import Card from '../components/Card.js';
import { getGamemodeColor, getGamemodeLabel } from '../lib/gamemode.js';
import { useNavigate } from 'react-router-dom';

const GAMEMODE_TABS = [
  { value: 'all', label: 'Overall' },
  { value: 'mace', label: 'Mace' },
  { value: 'sword', label: 'Sword' },
  { value: 'axe_shield', label: 'Axe' }
];

export default function Leaderboard() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [leaderboardList, setLeaderboardList] = useState<any[]>([]);
  const [selectedGamemode, setSelectedGamemode] = useState('all');
  const [isLoading, setIsLoading] = useState(true);

  const fetchLeaderboard = async () => {
    try {
      const gmQuery = selectedGamemode !== 'all' ? `?gamemode=${selectedGamemode}` : '';
      const response = await api.get(`/api/leaderboard${gmQuery}`);
      setLeaderboardList(response.data || []);
    } catch (err) {
      console.error('Failed to load leaderboard data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchLeaderboard();
  }, [selectedGamemode]);

  useEffect(() => {
    connectSocket();
    const socket = getSocket();

    const handleLeaderboardUpdate = () => {
      fetchLeaderboard();
    };

    socket.on('leaderboard:updated', handleLeaderboardUpdate);
    return () => {
      socket.off('leaderboard:updated', handleLeaderboardUpdate);
    };
  }, [selectedGamemode]);

  const topThree = leaderboardList.slice(0, 3);
  const restOfList = leaderboardList.slice(3);

  const podiumOrder = [
    topThree.find((r) => r.rank === 2),
    topThree.find((r) => r.rank === 1),
    topThree.find((r) => r.rank === 3)
  ];

  const getDefaultAvatar = (username: string) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(username)}&background=1a1a1a&color=e03c3c&size=120&bold=true&format=svg`;

  if (isLoading) {
    return (
      <div className="flex-1 min-h-screen bg-background flex items-center justify-center text-on-surface-variant font-medium">
        <div className="w-full max-w-6xl px-gutter">
          <div className="grid grid-cols-12 gap-gutter">
            <div className="col-span-12 mb-6">
              <Skeleton height={64} />
            </div>
            <div className="col-span-12 grid grid-cols-12 gap-gutter">
              <div className="col-span-4 h-64">
                <Skeleton />
              </div>
              <div className="col-span-8">
                <div className="grid grid-cols-1 gap-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="h-12 rounded-lg overflow-hidden">
                      <Skeleton />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-background text-on-surface select-none">
      <div className="mb-8">
        <h1 className="font-headline-lg text-headline-lg text-on-surface mb-2">Global Leaderboard</h1>
        <p className="text-on-surface-variant text-body-sm">
          Top combatants ranked by XP earned across training modules.
        </p>
      </div>

      <div className="flex bg-surface-container-high p-1 rounded-sm gap-1 mb-8 w-fit">
        {GAMEMODE_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => setSelectedGamemode(tab.value)}
            className={`px-4 py-2 font-button text-button rounded-sm transition-colors ${
              selectedGamemode === tab.value
                ? 'bg-surface-variant text-on-surface border border-outline-variant/30'
                : 'text-on-surface-variant hover:bg-surface-container-highest'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {topThree.length > 0 && (
        <div className="grid grid-cols-12 gap-gutter mb-12 items-end">
          {podiumOrder.map((row, colIndex) => {
            if (!row) return <div key={colIndex} className="col-span-4" />;

            const isFirst = row.rank === 1;
            const isSecond = row.rank === 2;
            const avatarSrc = row.avatarUrl || getDefaultAvatar(row.username);
            return (
              <div
                key={row.userId}
                className="col-span-4 podium-slide cursor-pointer"
                style={{ animationDelay: `${colIndex * 100}ms` }}
                onClick={() => navigate(`/profile/${row.username}`)}
              >
                <Card
                  className={`p-stack-lg flex flex-col items-center text-center relative justify-center ${
                    isFirst ? 'h-80 border-error/30 ring-1 ring-error/10' : isSecond ? 'h-72' : 'h-64'
                  }`}
                >
                  <div
                    className={`absolute left-1/2 -translate-x-1/2 rounded-sm font-black ${
                      isFirst
                        ? '-top-6 bg-[#e03c3c] text-white text-[32px] px-6 py-2'
                        : '-top-4 bg-surface-variant border border-outline-variant text-on-surface font-bold px-4 py-1 ' +
                          (isSecond ? 'text-[24px]' : 'text-[20px]')
                    }`}
                  >
                    {row.rank}
                  </div>

                  <div
                    className={`rounded-full p-1 mb-4 ${
                      isFirst
                        ? 'w-24 h-24 border-4 border-[#e03c3c]'
                        : isSecond
                        ? 'w-20 h-20 border-4 border-[#c0c0c0]'
                        : 'w-16 h-16 border-4 border-[#cd7f32]'
                    }`}
                  >
                    <img
                      alt={row.username}
                      className="rounded-full w-full h-full object-cover"
                      src={avatarSrc}
                    />
                  </div>

                  <h3
                    className={`mb-1 truncate max-w-full px-2 ${
                      isFirst ? 'font-headline-lg text-headline-lg text-[#e03c3c]' : 'font-headline-sm text-headline-sm'
                    }`}
                  >
                    {row.username}
                  </h3>
                  <div className={`mb-4 ${isFirst ? 'text-on-surface font-bold text-lg' : 'text-on-surface-variant'}`}>
                    {row.totalXp.toLocaleString()} XP
                  </div>
                  <Badge variant="rank">{row.rankTitle}</Badge>
                  {isFirst && row.rank === 1 && (
                    <Badge className="mt-2 ml-0 bg-[#e03c3c]/10 text-white border border-[#e03c3c]/20">
                      Champion
                    </Badge>
                  )}
                </Card>
              </div>
            );
          })}
        </div>
      )}

      <Card className="rounded-xl overflow-hidden mb-8">
        <table className="w-full text-left border-collapse">
          <thead className="bg-surface-container-high border-b border-outline-variant">
            <tr>
              <th className="px-6 py-4 font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">Rank</th>
              <th className="px-6 py-4 font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">Player</th>
              <th className="px-6 py-4 font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">XP Score</th>
              <th className="px-6 py-4 font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">Skill Badges</th>
              <th className="px-6 py-4 font-label-caps text-label-caps text-on-surface-variant uppercase tracking-wider">Rank Title</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/30">
            {restOfList.map((row) => {
              const isMe = row.userId === user?.id;
              const avatarSrc = row.avatarUrl || getDefaultAvatar(row.username);

              return (
                <tr
                  key={row.userId}
                  className={`hover:bg-surface-container transition-colors group cursor-pointer ${
                    isMe ? 'bg-surface-variant/40 border-l-2 border-error' : ''
                  }`}
                  onClick={() => navigate(`/profile/${row.username}`)}
                >
                  <td className="px-6 py-4 font-bold text-on-surface-variant group-hover:text-on-surface">
                    #{String(row.rank).padStart(2, '0')}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        alt={row.username}
                        className="w-8 h-8 rounded-full border object-cover"
                        style={{ borderColor: row.accentColor || 'rgba(255,255,255,0.15)' }}
                        src={avatarSrc}
                      />
                      <span className={`font-button text-button ${isMe ? 'text-on-surface font-semibold' : ''}`}>
                        {row.username}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-on-surface-variant font-mono">
                    {row.totalXp.toLocaleString()}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="rank">{row.rankTitle}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    {selectedGamemode !== 'all' ? (
                      <span className={`px-2 py-1 font-label-caps text-[11px] rounded-[4px] ${getGamemodeColor(selectedGamemode)}`}>
                        {getGamemodeLabel(selectedGamemode)}
                      </span>
                    ) : (
                      <span className="text-on-surface-variant text-[12px]">All modes</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {leaderboardList.length === 0 && (
          <div className="p-8 text-center text-on-surface-variant">
            No rankings yet. Complete lessons to earn XP and climb the board.
          </div>
        )}
      </Card>
    </div>
  );
}